/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GiPrayer } from "react-icons/gi";
import { HiOutlinePencil, HiOutlineTrash } from "react-icons/hi";

import ListPage from "../../layout/ListPage";
import type { Action, Column } from "../../ui";
import { useReligionContext } from "../../context/ReligionContext";
import { pushOneReligion } from "../../lib/sysNavigation/SysNavigation";
import { _PAGE_SIZE_ } from "../../lib/other/tools";

type Religion = {
  religion_id: number | string;
  name: string;
  [key: string]: unknown;
};

const addRoute = "/religion/add";
const editRoute = "/religion/edit";

const Overview: React.FC = () => {
  const navigate = useNavigate();
  const {
    religion,
    count,
    fetchReligions,
    deleteReligion,
    updateReligionState,
  } = useReligionContext();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(_PAGE_SIZE_);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!religion) fetchReligions(page, pageSize);
  }, [religion]);

  const rows = useMemo<Religion[] | null>(() => {
    if (!Array.isArray(religion)) return null;
    const q = query.trim().toLowerCase();
    if (!q) return religion as Religion[];
    return (religion as Religion[]).filter((r) =>
      r.name?.toString().toLowerCase().includes(q),
    );
  }, [religion, query]);

  const columns: Column<Religion>[] = [
    {
      header: "Name",
      cell: (row) => (
        <div className="flex items-center gap-3">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand/10 text-brand ring-1 ring-inset ring-brand/15">
            <GiPrayer className="h-4 w-4" />
          </span>
          <span className="font-medium text-gray-900">{row.name}</span>
        </div>
      ),
    },
  ];

  const handleDelete = async (row: Religion) => {
    const response: any = await deleteReligion(row.religion_id);
    if (response?.data?.status === 200 && Array.isArray(religion)) {
      updateReligionState(
        (religion as Religion[]).filter((r) => r.religion_id !== row.religion_id),
      );
    }
  };

  const rowActions = (row: Religion): Action[] => [
    {
      label: "Edit",
      icon: <HiOutlinePencil className="h-3.5 w-3.5" />,
      onClick: () => {
        pushOneReligion(row);
        navigate(editRoute);
      },
    },
    {
      label: "Delete",
      variant: "destructive",
      icon: <HiOutlineTrash className="h-3.5 w-3.5" />,
      onClick: () => handleDelete(row),
      confirm: {
        title: `Delete ${row.name}?`,
        description:
          "The religion will be removed from any country or record that references it.",
        confirmLabel: "Delete",
      },
    },
  ];

  return (
    <ListPage<Religion>
      title="Religions"
      subtitle="Browse, add and manage religion reference data."
      icon={<GiPrayer className="h-5 w-5" />}
      actions={[{ label: "Add religion", variant: "primary", href: addRoute }]}
      rows={rows}
      columns={columns}
      rowKey={(r) => r.religion_id}
      rowActions={rowActions}
      search={{
        value: query,
        onChange: setQuery,
        placeholder: "Search religions…",
      }}
      pagination={{
        page,
        pageSize,
        total: count,
        onChange: ({ page: p, pageSize: ps }) => {
          setPage(p);
          setPageSize(ps);
          fetchReligions(p, ps);
        },
      }}
      empty="No religions yet."
    />
  );
};

export default Overview;
